// ============================================================
// Bannière d'abonnement — affichée en haut de toutes les pages
// ============================================================
//
// 1) ESSAI : tant que le commerce est en période d'essai, une bande
//    s'affiche en permanence avec le compteur de jours restants
//    (et un rappel « Passez en Premium »). À l'expiration → bande rouge.
//
// 2) PREMIUM : avertissement seulement à l'approche / au dépassement
//    de l'échéance (J-15 → expiré).
//
// L'info est aussi mise en cache (localStorage) pour pouvoir afficher
// le compteur d'essai sur la page de connexion (hors session).
// ============================================================
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { AlertTriangle, X, Calendar, Sparkles } from 'lucide-react';

const STORAGE_KEY_DISMISS = 'sv_abo_banner_dismissed_at';
const DISMISS_DUREE_H = 12;
const CACHE_ABO = 'sv_cached_abo';

function dismissValide() {
  try {
    const t = localStorage.getItem(STORAGE_KEY_DISMISS);
    if (!t) return false;
    return (Date.now() - Number(t)) < DISMISS_DUREE_H * 3600 * 1000;
  } catch { return false; }
}

export default function AbonnementBanner() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(dismissValide());
  const navigate = useNavigate();
  // Sur la page « Mon abonnement » elle-même, le bouton « Passer en Premium »
  // de ce bandeau global ferait juste naviguer vers la page courante : on le
  // masque pour éviter d'avoir plusieurs boutons identiques sur cette page.
  const surPageAbonnement = useLocation().pathname === '/abonnement';

  useEffect(() => {
    api.get('/mon-abonnement')
      .then((r) => {
        setData(r.data);
        // Mémorise l'essai pour l'afficher sur la page de connexion.
        try {
          if (r.data?.a_un_abonnement) {
            localStorage.setItem(CACHE_ABO, JSON.stringify({
              plan_code: r.data.plan_code,
              statut: r.data.statut,
              date_expiration: r.data.date_expiration,
            }));
          } else {
            localStorage.removeItem(CACHE_ABO);
          }
        } catch {}
      })
      .catch(() => setData(null));
  }, []);

  if (!data || !data.a_un_abonnement) return null;

  const estEssai = data.plan_code === 'essai';

  // ── 1. ESSAI ───────────────────────────────────────────────
  if (estEssai) {
    const jours = data.jours_restants;
    if (data.statut === 'lecture_seule') {
      return (
        <div className="bg-red-600/20 border-b border-red-500/60 px-4 py-2.5 flex items-center gap-3">
          <AlertTriangle size={16} className="text-red-300 flex-shrink-0" />
          <div className="flex-1 text-sm text-red-100">
            <span className="font-medium">🔒 Votre essai gratuit est terminé : accès en lecture seule.</span>
            <span className="ml-1 opacity-90">Passez en Premium pour réactiver la saisie.</span>
          </div>
          {!surPageAbonnement && (
            <button onClick={() => navigate('/abonnement')}
              className="text-xs font-semibold bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg whitespace-nowrap">
              Passer en Premium
            </button>
          )}
        </div>
      );
    }
    const libelleJours = jours > 1 ? `${jours} jours restants`
      : jours === 1 ? 'dernier jour'
      : jours === 0 ? 'dernier jour (expire aujourd\'hui)'
      : 'expire bientôt';
    return (
      <div className="bg-amber-500/10 border-b border-amber-500/40 px-4 py-2.5 flex items-center gap-3">
        <Sparkles size={16} className="text-amber-400 flex-shrink-0" />
        <div className="flex-1 text-sm text-amber-200">
          <span className="font-medium">🎁 Période d'essai gratuite — {libelleJours}.</span>
          {data.date_expiration && (
            <span className="ml-2 text-xs opacity-80 inline-flex items-center gap-1">
              <Calendar size={11} /> jusqu'au {new Date(data.date_expiration).toLocaleDateString('fr-FR')}
            </span>
          )}
        </div>
        {!surPageAbonnement && (
          <button onClick={() => navigate('/abonnement')}
            className="text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-gray-900 px-3 py-1.5 rounded-lg whitespace-nowrap">
            Passer en Premium
          </button>
        )}
      </div>
    );
  }

  // ── 2. PREMIUM (et autres plans payants) : alerte près de l'échéance ──
  if (data.alerte === 'aucune') return null;

  const styles = {
    expiration_proche: {
      bg: 'bg-amber-500/10 border-amber-500/40', text: 'text-amber-200', icon: 'text-amber-400',
      dismissable: true,
      label: `⏰ Votre abonnement expire dans ${data.jours_restants} jour${data.jours_restants > 1 ? 's' : ''}.`,
    },
    expire_aujourdhui: {
      bg: 'bg-orange-500/15 border-orange-500/50', text: 'text-orange-200', icon: 'text-orange-400',
      dismissable: false,
      label: `🚨 Votre abonnement expire aujourd'hui. Renouvelez-le pour éviter le passage en lecture seule.`,
    },
    en_grace: {
      bg: 'bg-red-500/15 border-red-500/50', text: 'text-red-200', icon: 'text-red-400',
      dismissable: false,
      label: `Votre abonnement est expiré. Renouvelez-le rapidement.`,
    },
    lecture_seule: {
      bg: 'bg-red-600/20 border-red-500/60', text: 'text-red-100', icon: 'text-red-300',
      dismissable: false,
      label: `🔒 Abonnement expiré : lecture seule activée. Toute saisie est bloquée jusqu'au renouvellement.`,
    },
  };

  const cfg = styles[data.alerte];
  if (!cfg) return null;
  if (cfg.dismissable && dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY_DISMISS, String(Date.now())); } catch {}
    setDismissed(true);
  };

  return (
    <div className={`${cfg.bg} border-b px-4 py-2.5 flex items-center gap-3`}>
      <AlertTriangle size={16} className={`${cfg.icon} flex-shrink-0`} />
      <div className={`flex-1 text-sm ${cfg.text}`}>
        <span className="font-medium">{cfg.label}</span>
        {data.date_expiration && (
          <span className="ml-2 text-xs opacity-80 inline-flex items-center gap-1">
            <Calendar size={11} />
            Expire le {new Date(data.date_expiration).toLocaleDateString('fr-FR')}
          </span>
        )}
        {data.plan_nom && <span className="ml-2 text-xs opacity-70">· Plan {data.plan_nom}</span>}
      </div>
      {cfg.dismissable && (
        <button onClick={handleDismiss}
          className={`p-1 rounded hover:bg-white/10 ${cfg.text}`}
          title="Masquer pour 12 heures">
          <X size={14} />
        </button>
      )}
    </div>
  );
}
